// @ts-strict-ignore
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	output,
} from "@angular/core";
import {Translations} from "@translations";
import {AssessmentState, Participant, Permissions, ScenarioRecording, SimUser, SuccessResponse, UserRecordingAssessment} from "@models";
import {SimAssessmentService, SimMaterialDialogService, NotificationService, SimUserExtendedService, TrackByMethods} from "@services";
import {DetachableChangeDetection} from "../panel-components/detachable-cd/detachable-change-detection";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {EventEmitterService} from "@event-emitter";
import {TranslateService} from "@ngx-translate/core";
import {UntilDestroy} from "@ngneat/until-destroy";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {AssessmentAnswerPanelVariant} from "../assessable-panels/assessment-answer-panel/assessment-answer-panel.component";

@UntilDestroy()
@Component({
	selector: "sim-assessment-pane",
	templateUrl: "./assessment-pane.component.html",
	styleUrls: ["./assessment-pane.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 300}), zoomOutOnLeaveAnimation({duration: 300})],
	providers: [EventEmitterService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AssessmentPaneComponent implements OnInit, DetachableChangeDetection, OnChanges {
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly Array = Array;
	protected readonly AssessmentState = AssessmentState;
	protected readonly T = Translations;
	protected readonly TrackByMethods = TrackByMethods;
	protected readonly AssessmentAnswerPanelVariant = AssessmentAnswerPanelVariant;

	@Input() participations: Participant[] = [];
	/**
	 * If true, the component will load all assessments for the recording if the current user has the permission,
	 * otherwise only user created assessments are loaded.
	 */
	@Input() isInArchive = false;
	@Input() scenarioRecordingId: string;
	@Input() scenarioRecording: ScenarioRecording;

	jumpToItem = output<number>();

	userRecordingAssessments: UserRecordingAssessment[] = [];
	selectedTabIndex: number;

	assessmentCandidatesPerAssessment: Map<string, SimUser[]> = new Map();

	constructor(
		private _simAssessmentService: SimAssessmentService,
		private _changeDetector: ChangeDetectorRef,
		private _simNotificationService: NotificationService,
		private _dialogService: SimMaterialDialogService,
		private _simUserService: SimUserExtendedService,
		private _emitterService: EventEmitterService,
		private _translateService: TranslateService,
		private _simMaterialDialogService: SimMaterialDialogService,
	) {
		// called on a change like finished, aborted, reopened
		this._emitterService.onUserRecordingAssessmentChanged.subscribe(() => this.loadUserRecordingAssessments());
	}

	ngOnInit() {
		this.loadUserRecordingAssessments().then();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.scenarioRecordingId) {
			this.loadUserRecordingAssessments().then();
		}
	}

	attachToCD() {
		this._changeDetector.reattach();
	}

	detachFromCD() {
		this._changeDetector.detach();
	}

	private async loadUserRecordingAssessments() {
		if (!this.scenarioRecordingId) {
			return;
		}

		const canLoadAll = this._simUserService.hasPermission(Permissions.Assessments_Conducted_AccessAll);
		let loadingTask: Promise<UserRecordingAssessment[]>;

		if (this.isInArchive && canLoadAll) {
			loadingTask = this._simAssessmentService.getAllUserRecordingAssessments(this.scenarioRecordingId);
		} else {
			loadingTask = this._simAssessmentService.getUserRecordingAssessments(this.scenarioRecordingId);
		}

		this.userRecordingAssessments = await loadingTask.catch<[]>((_) => {
			this._simNotificationService.notifyError(this._translateService.instant(this.T.ASSESSMENT_PANE_COULD_NOT_LOAD));
			return [];
		});

		const tasks = this.userRecordingAssessments.map(async (assessment) => {
			return [
				assessment.id,
				await this._simUserService.getUsersByIds({
					userIds: assessment.appraiseeIds,
				}),
			] as const;
		});
		await Promise.all(tasks).then((results) => {
			this.assessmentCandidatesPerAssessment.clear();
			results.forEach(([assessmentId, users]) => {
				this.assessmentCandidatesPerAssessment.set(assessmentId, users);
			});
		});

		this._changeDetector.markForCheck();
	}

	async finishAssessment(userRecordingAssessment: UserRecordingAssessment) {
		const result = await this._simAssessmentService
			.finishAssessment({
				userRecordingAssessmentId: userRecordingAssessment.id,
			})
			.catch<SuccessResponse>((_) => {
				this._simNotificationService.notifyError(this._translateService.instant(this.T.ASSESSMENT_PANE_COULD_NOT_FINISH));
				return {success: false, reason: null};
			});

		if (result.success) {
			this._simNotificationService.notifySuccess(this._translateService.instant(this.T.ASSESSMENT_PANE_SUCCESSFUL_FINISH));
		}
	}

	async abortAssessment(userRecordingAssessment: UserRecordingAssessment) {
		const dialogResult = await this._dialogService.openAbortAssessmentDialog();

		if (dialogResult.action !== "ok") {
			return;
		}

		const result = await this._simAssessmentService
			.abortAssessment({
				userRecordingAssessmentId: userRecordingAssessment.id,
				reason: dialogResult.reason,
			})
			.catch<SuccessResponse>((_) => {
				this._simNotificationService.notifyError(this._translateService.instant(this.T.ASSESSMENT_PANEL_COULD_NOT_ABORT));
				return {success: false, reason: null};
			});

		if (result.success) {
			this._simNotificationService.notifySuccess(this._translateService.instant(this.T.ASSESSMENT_PANE_SUCCESSFUL_ABORT));
		}
	}

	async addAssessment() {
		const {action, selectedAssessment, selectedParticipants} = await this._simMaterialDialogService.openAssignAssessmentDialog({
			participants: this.scenarioRecording.participants.map((learner) => learner.user),
			scenarioRecordingId: this.scenarioRecording.id,
		});

		if (action !== "ok") {
			return;
		}

		const userRecordingAssessment = await this._simAssessmentService
			.createUserRecordingAssessment({
				assessmentId: selectedAssessment.id,
				appraiseeIds: selectedParticipants.map((participant) => participant.id),
				scenarioRecordingId: this.scenarioRecording.id,
			})
			.catch<null>((_) => null);

		if (!userRecordingAssessment) {
			this._simNotificationService.notifyError(this._translateService.instant(this.T.ASSESSMENT_PANE_COULD_NOT_START));
			return;
		}
	}

	getAssessmentName(userRecordingAssessment: UserRecordingAssessment & {id: string}) {
		if (userRecordingAssessment?.name?.length > 20) {
			return userRecordingAssessment?.name?.substring(0, 19) + "...";
		} else {
			return userRecordingAssessment?.name;
		}
	}

	async reopenAssessment(userRecordingAssessment: UserRecordingAssessment) {
		const result = await this._simAssessmentService
			.reopenAssessment({
				userRecordingAssessmentId: userRecordingAssessment.id,
			})
			.catch<SuccessResponse>((_) => {
				this._simNotificationService.notifyError(this._translateService.instant(this.T.ASSESSMENT_REVIEW_COULD_NOT_REOPEN));
				return {success: false, reason: null};
			});

		if (result.success) {
			this._simNotificationService.notifySuccess(this._translateService.instant(this.T.CLIENT_REGISTER_TITLE_TABLET_SUCCESS));
		}
	}

	onScroll() {
		(document.activeElement as any)?.blur?.();
	}
}
